import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'user';
  profile_completed: boolean;
  disclaimer_accepted: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    isAdmin: false,
  });

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Errore nel caricamento del profilo utente:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Errore nel fetch del profilo:', error);
      return null;
    }
  };

  const updateAuthState = async (session: Session | null) => {
    if (session?.user) {
      const profile = await fetchUserProfile(session.user.id);
      setAuthState({
        user: session.user,
        profile,
        session,
        loading: false,
        isAdmin: profile?.role === 'admin',
      });
    } else {
      setAuthState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        isAdmin: false,
      });
    }
  };

  const handleAuthError = async (error: any) => {
    console.error('Auth error:', error);
    
    // Handle refresh token errors by signing out
    if (error?.message?.includes('refresh_token_not_found') || 
        error?.message?.includes('Invalid Refresh Token') ||
        error?.code === 'refresh_token_not_found') {
      console.log('Refresh token invalid, signing out user');
      await supabase.auth.signOut();
      return;
    }
    
    // Handle other auth errors
    if (error?.message?.includes('JWT') || 
        error?.message?.includes('token') ||
        error?.status === 401) {
      console.log('Authentication token invalid, signing out user');
      await supabase.auth.signOut();
      return;
    }
  };

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting initial session:', error);
          await handleAuthError(error);
          setAuthState(prev => ({ ...prev, loading: false }));
          return;
        }
        await updateAuthState(session);
      } catch (error) {
        console.error('Error in getInitialSession:', error);
        await handleAuthError(error);
        setAuthState(prev => ({ ...prev, loading: false }));
      }
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        // Handle specific auth events
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
        } else if (event === 'SIGNED_IN') {
          console.log('User signed in');
        }
        
        try {
          await updateAuthState(session);
        } catch (error) {
          console.error('Error updating auth state:', error);
          await handleAuthError(error);
        }
      }
    );

    // Set up periodic session refresh (every 50 minutes)
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error('Error refreshing session:', error);
            await handleAuthError(error);
          }
        }
      } catch (error) {
        console.error('Error in periodic refresh:', error);
        await handleAuthError(error);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error during logout:', error);
        // Even if there's an error, clear the local state
        setAuthState({
          user: null,
          profile: null,
          session: null,
          loading: false,
          isAdmin: false,
        });
        throw error;
      }
    } catch (error) {
      console.error('Error in logout:', error);
      // Clear local state even on error
      setAuthState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        isAdmin: false,
      });
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (authState.user) {
      const profile = await fetchUserProfile(authState.user.id);
      setAuthState(prev => ({
        ...prev,
        profile,
        isAdmin: profile?.role === 'admin',
      }));
    }
  };

  return {
    ...authState,
    signOut,
    refreshProfile,
  };
};